'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';

interface RadiantProduct {
  id: number;
  shop_id?: number;
  shop_sku: string;
  name: string;
  short_name: string;
  introduction?: string;
  product_image: string;
  main_img?: string;
  price_info: any;
  price_info_v1?: any;
  price_info_v2?: any;
  price_info_v3?: any;
  discount_percent_v1?: number;
  discount_percent_v2?: number;
  discount_percent_v3?: number;
}

interface DivaRadiant1Props {
  pageData: {
    radiant1: RadiantProduct[];
    radiant1_title_image: string;
  };
  deviceType: EPlatform;
}

export default function DivaRadiant1({ pageData, deviceType }: DivaRadiant1Props) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();

  const isWap = deviceType !== EPlatform.WEB;

  // 安全检查：如果没有商品数据，返回null
  if (!pageData.radiant1 || pageData.radiant1.length === 0) {
    return null;
  }

  // 使用公共的价格处理方法 - 与Vue版本的priceInfo方法一致
  const getPriceInfo = (item: RadiantProduct) => {
    return priceInfo(userInfo, item, currency);
  };

  // 商品点击处理 - 使用navigate方法统一处理跳转
  const handleProductClick = (item: RadiantProduct) => {
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  if (isWap) {
    // 移动端布局 - 基于WAP版本Radiant1.vue
    return (
      <div className="diva-radiant1 bg-black">
        <Image
          className="title-image w-[375px]"
          src={pageData.radiant1_title_image}
          alt="Keep exploring the sparking glasses showcase your full charm banner1"
          width={375}
          height={200}
        />

        <ul>
          <li className="mt-[15px] w-[345px] mx-auto">
            {pageData.radiant1.map((item, index) => {
              const currentPriceInfo = getPriceInfo(item);
              return (
                <a
                  key={item.shop_id || item.id || index}
                  className="product-item inline-block relative mb-[15px] w-full"
                  href={`/goods-detail/${item.shop_sku}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleProductClick(item);
                  }}
                >
                  <Image
                    className="product-img w-full"
                    src={item.product_image || item.main_img}
                    alt={item.name}
                    width={345}
                    height={400}
                  />
                  <div className="product-info absolute w-[104px] h-auto right-0 top-[88px] box-border text-center">
                    <div className="product-name font-bb font-bold text-[12px] text-primary-black">
                      {item.short_name}
                    </div>
                    <div className="price font-bb text-[15px] text-[#DA974B] pt-[4px] notranslate">
                      {formatPriceNumberWithSymbol(currentPriceInfo.sell_price || '0', currency)}
                    </div>
                    {currentPriceInfo.sell_price !== currentPriceInfo.price &&
                      currentPriceInfo.price && (
                        <div className="old-price font-br font-medium text-[10px] text-secondary-text line-through notranslate">
                          {formatPriceNumberWithSymbol(currentPriceInfo.price, currency)}
                        </div>
                      )}
                  </div>
                </a>
              );
            })}
          </li>
        </ul>
      </div>
    );
  }

  // PC端布局 - 基于PC版本Radiant1.vue
  return (
    <div className="like-dianond bg-black pt-[1.56vw]">
      <Image
        className="title-image w-full"
        src={pageData.radiant1_title_image}
        alt="Keep exploring the sparking glasses showcase your full charm banner1"
        width={1920}
        height={400}
      />

      <div className="product-row w-full px-[6.25vw] box-border flex justify-between">
        {pageData.radiant1.map((item, index) => {
          const currentPriceInfo = getPriceInfo(item);
          return (
            <a
              key={item.shop_id || item.id || index}
              href={`/goods-detail/${item.shop_sku}`}
              className={`product-item inline-block w-[28.28vw] relative ${index < 2 ? 'mr-[0.83vw]' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                handleProductClick(item);
              }}
            >
              <Image
                className="product-img w-[28.28vw]"
                src={item.product_image || item.main_img}
                alt={item.name}
                width={543}
                height={600}
              />
              <div className="bottom-box w-[28.28vw] h-[6.04vw] box-border absolute px-[1.15vw] py-[1.35vw] left-0 right-0 bottom-0">
                <div className="flex justify-between items-end">
                  <div className="short-name font-bb text-[2.08vw] leading-[2.48vw] text-primary-black">
                    {item.short_name}
                  </div>
                  <div className="price font-bb text-[1.61vw] leading-[1.93vw] text-[#DA974B] notranslate">
                    {formatPriceNumberWithSymbol(currentPriceInfo.sell_price || '0', currency)}
                  </div>
                </div>
                <div className="flex justify-between items-start">
                  <div className="introduction font-br text-[0.99vw] leading-[1.19vw] text-secondary-black max-w-[16.67vw] box-border">
                    {item.introduction}
                  </div>
                  {currentPriceInfo.sell_price !== currentPriceInfo.price &&
                    currentPriceInfo.price && (
                      <div className="old-price font-br text-[1.25vw] leading-[1.5vw] text-secondary-text line-through notranslate">
                        {formatPriceNumberWithSymbol(currentPriceInfo.price, currency)}
                      </div>
                    )}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
